import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { take } from "rxjs/operators";
import { CalculationService } from "../../../services/calculations/calculation.service";
import { DealService } from "../../../services/deal/deal.service";
import { FinanceOptions, FinancingSettings } from "../../../models";
import { AppService } from "src/app/clearpath-module/services";
import { combineLatest } from "rxjs";

@Component({
  selector: "app-roll-payment-calculator",
  templateUrl: "./roll-payment-calculator.component.html",
  styleUrls: ["./roll-payment-calculator.component.scss"]
})
export class RollPaymentCalculatorComponent implements OnInit {
  @Output() formSubmitFlag = new EventEmitter();
  interestRate: number;
  financeOptions: FinanceOptions;
  financing: FinancingSettings;
  rollPaymentForm: UntypedFormGroup = this.formBuilder.group({
    desiredPayment: [400, Validators.required],
    downPayment: [0, Validators.required],
    numberOfMonthlyPayments: [60, Validators.required]
  });

  constructor(
    private formBuilder: UntypedFormBuilder,
    private calculationService: CalculationService,
    private dealService: DealService,
    private appService: AppService,
  ) { }

  ngOnInit() {
    this.subToFinanceOptions();
    this.subToFinancing();
    this.calcDownPayment();
  }

  private subToFinanceOptions() {
    this.dealService.selectFinanceOptions()
      .pipe(take(1))
      .subscribe((financeOptions: FinanceOptions) => {
        const {
          desiredPayment,
          numberOfMonthlyPayments
        } = financeOptions;
        this.financeOptions = financeOptions;
        if (desiredPayment) {
          this.rollPaymentForm.patchValue({desiredPayment});
        }
        if (numberOfMonthlyPayments) {
          this.rollPaymentForm.patchValue({numberOfMonthlyPayments});
        }
      });
  }

  private subToFinancing() {
    this.appService.selectFinancing()
      .pipe(take(1))
      .subscribe((financing) => {
        this.financing = financing;
      });
  }

  // ACTIONS
  calcDownPayment() {
    if (!this.rollPaymentForm.valid) { return; }
    const {desiredPayment, numberOfMonthlyPayments} = this.rollPaymentForm.value;

    combineLatest([
      this.calculationService.findInterestRate$({term: numberOfMonthlyPayments}),
      this.calculationService.totalVehicleFinancePrice$({
        term: numberOfMonthlyPayments,
        finance: true,
        excludeDown: true,
        withoutDaysToFirstPay: true
      }),
    ])
      .pipe(take(1))
      .subscribe(([interestRate, totalVehicleFinancePrice]) => {
        this.interestRate = interestRate;

        const downPayment = this.calculationService.getDownPayment(
          this.interestRate,
          totalVehicleFinancePrice,
          desiredPayment,
          numberOfMonthlyPayments,
          0, // trade equity is already included in totalVehicleFinancePrice
          this.financeOptions.daysToFirstPay || this.financing.financeDefault.daysToFirstPay
        );
        this.rollPaymentForm.patchValue({downPayment});
        this.rollPaymentForm.markAsDirty();
      });
  }

  onSubmitRollPaymentForm() {
    const {desiredPayment, downPayment, numberOfMonthlyPayments} = this.rollPaymentForm.value;
    this.dealService.submitRollPaymentForm(
      desiredPayment,
      this.calculationService.calculationUtilityService.truncateDecimal(downPayment, 0),
      numberOfMonthlyPayments
    );
    this.formSubmitFlag.emit();
  }

  // FORM VALIDATION & HELPERS
  touchedInvalid(controlName: string): boolean {
    const touched = this.rollPaymentForm.controls[ controlName ].touched;
    const valid = this.rollPaymentForm.controls[ controlName ].valid;
    return touched && !valid;
  }
}
